import React, { Component, Fragment } from 'react';
import { withRouter } from 'react-router';

import { Accordion } from 'components/accordion';
import { Checkbox } from 'components/cosmos/checkbox';
import { Form, FormControl } from 'components/form';
import { Grid } from 'components/grid';
import {
  InputGroup,
  EditorGroup,
  MediaUploadGroup,
  DrippingGroup
} from 'components/input';
import { Loader } from 'components/loader';
import { I18n } from 'components/translate';
import { ROUTES } from 'configs/routes/constants';
import { normalizeDrippingData } from 'helpers/Dripping';
import { toJS } from 'mobx';
import { inject, observer, PropTypes as MobxPropTypes } from 'mobx-react';
import { DatahubEventsService } from 'modules/app/services';
import { ClickAdvertisementEditorSaveSchema } from 'modules/app/services/datahubEvents/advertisement/clickAdvertisementEditorSaveSchema';
import { ViewAdvertisementEditorSchema } from 'modules/app/services/datahubEvents/advertisement/viewAdvertisementEditorSchema';
import ContentBasePage from 'modules/content/components/ContentBasePage';
import ContentPreventLeave from 'modules/content/components/ContentPreventLeave';
import Language from 'modules/content/components/language/Language';
import { Rerender } from 'modules/content/components/reRender';
import ContentPageService from 'modules/content/ContentPageService';
import {
  CONTENT_PAGE_TYPE,
  getContentPageTitle
} from 'modules/content/contentPageTitles';
import ContentService from 'modules/content/ContentService';
import DrippingSection from 'modules/content/dripping/components/drippingSection';
import { DrippingStore } from 'modules/content/dripping/stores';
import { PageType } from 'modules/content/moduleService/ModuleTypes';
import { FeatureToggles } from 'modules/featureToggle';
import { FileService } from 'modules/file';
import { ENTITY_TYPE } from 'modules/file/constants';
import { UiService } from 'modules/ui';
import PropTypes from 'prop-types';
import QueryString from 'query-string';

import OfferRadiolist from './OfferRadiolist';
import ProductRadiolist from './ProductRadiolist';

import DrippingValidation from '../../components/dripping/DrippingValidation';
import AdvertisementEditorPreventLeaveService from '../AdvertisementEditorPreventLeaveService';
import AdvertisementEditorService from '../AdvertisementEditorService';

import './AdvertisementEditor.scss';

@inject((stores) => ({
  AdvertisementEditorStore: stores.AdvertisementEditorStore,
  isDrippingByClassEnabled: stores.FeatureToggleStore.featureToggles.get(
    FeatureToggles.DrippingByClass
  ),
  isDrippingDirty: stores.DrippingStore.isDirty,
  ContentListConceptToggle: stores.FeatureToggleStore.featureToggles.get(
    FeatureToggles.ContentListConcept
  )
}))
@withRouter
@observer
class AdvertisementEditor extends Component {
  // #region setup
  static propTypes = {
    AdvertisementEditorStore: MobxPropTypes.objectOrObservableObject,
    history: PropTypes.object,
    match: PropTypes.shape({
      params: PropTypes.shape({
        moduleId: PropTypes.string,
        pageId: PropTypes.string
      })
    })
  };

  state = {
    loaded: true,
    firstLoad: false,
    drippingErrors: DrippingValidation.initialState
  };

  constructor(props) {
    super(props);

    this.inputNameRef = React.createRef();
    this.reRenderRef = React.createRef();
  }

  componentDidMount() {
    const arrivedFrom = new URLSearchParams(window.location.search).get(
      'arrivedFrom'
    );
    const productId = new URLSearchParams(window.location.search).get(
      'productId'
    );
    const datahubViewSchema = new ViewAdvertisementEditorSchema({
      arrivedFrom,
      productId,
      path: this.props.match.url
    });
    const { moduleId } = this.props.match.params;

    DatahubEventsService.sendEvent({ schema: datahubViewSchema });
    ContentService.lastEditedModule = moduleId;
    AdvertisementEditorService.init({ moduleId });
    this.setInitialStateAsync();
  }

  componentWillUnmount() {
    AdvertisementEditorService.clear();
  }

  async setInitialStateAsync() {
    const {
      params: { pageId }
    } = this.props.match;

    if (pageId) {
      await AdvertisementEditorService.get(pageId);
    } else {
      AdvertisementEditorPreventLeaveService.updateSnapshot();
    }

    this.setState({ firstLoad: true });
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    UiService.smoothScroll('body', 500);
  }
  // #endregion

  // #region page actions
  get actions() {
    const actions = {};
    const { params } = this.props.match;

    if (params.pageId) {
      actions.onVisualize = this.visualize.bind(this);
      actions.onPublish = this.publish.bind(this);
    }

    return actions;
  }

  newAd = () => {
    this.toggleLoader();
    this.clear();

    this.reRenderRef.current?.reRender();
    this.inputNameRef.current?.focus();

    this.toggleLoader();
  };

  clear = () => {
    const { params } = this.props.match;

    this.formNode.setSubmitted(false);
    AdvertisementEditorService.clear();
    AdvertisementEditorService.moduleId = params.moduleId;
  };

  visualize = () => {
    const { pageId } = this.props.AdvertisementEditorStore;

    ContentPageService.contentPreview(pageId, null, this.seasonId);
  };

  publish = async () => {
    this.toggleLoader();

    const { ContentListConceptToggle } = this.props;
    const { pageId, published } = this.props.AdvertisementEditorStore;
    const modules = null;
    const showAlert = false;

    const response = await ContentPageService.publish(
      [pageId],
      !published,
      modules,
      showAlert
    );

    if (!ContentListConceptToggle) {
      await ContentService.fetchModulesList(this.seasonId);
    }

    if (response) {
      this.props.AdvertisementEditorStore.published = !published;
    }

    await this.formNode.submit();

    this.toggleLoader();
  };

  validateDripping = ({
    hasDuration,
    liberationType,
    liberationDays,
    liberationDate,
    durationDays
  }) => {
    const drippingValidation = new DrippingValidation(
      hasDuration,
      liberationType
    );

    this.setState({
      drippingErrors: drippingValidation.validate({
        liberationDays,
        liberationDate,
        durationDays
      })
    });

    return !drippingValidation.hasError;
  };

  handleSubmit = async (data) => {
    const seasonId = this.seasonId;

    data.dripping = normalizeDrippingData(data.dripping || {});

    if (!this.validateDripping(data.dripping)) {
      return;
    }

    this.toggleLoader();

    const response = await AdvertisementEditorService[this.actionType](data);

    if (response) {
      ContentService.fetchModulesList(seasonId, this.extra);
    }

    this.toggleLoader();

    return {
      data,
      response
    };
  };

  saveThenNavigate = async () => {
    const productId = new URLSearchParams(window.location.search).get(
      'productId'
    );
    const datahubViewSchema = new ClickAdvertisementEditorSaveSchema({
      productId,
      path: this.props.match.url
    });

    DatahubEventsService.sendEvent({ schema: datahubViewSchema });

    const {
      data: { createAnotherField },
      response
    } = await this.formNode.submit();

    DrippingStore.clear();

    if (response && response.id && createAnotherField) {
      this.newAd();

      return;
    }

    if (response !== false) {
      const { ContentListConceptToggle, match } = this.props;

      const currentParams = new URLSearchParams(window.location.search);

      if (ContentListConceptToggle) {
        const { moduleId, pageId } = match.params;

        currentParams.set('moduleId', moduleId);
        currentParams.set('pageId', pageId || response.id);
        currentParams.set('arrivedFrom', 'content');
      }

      this.props.history.push(
        `${ROUTES.CONTENT.LIST}?${currentParams.toString()}`
      );
    }
  };
  // #endregion

  // #region getters & setters
  get actionType() {
    return this.props.AdvertisementEditorStore.pageId ? 'update' : 'create';
  }

  get seasonId() {
    const qs = QueryString.parse(location.search);

    return qs.season ? qs.season : null;
  }

  get extra() {
    const qs = QueryString.parse(location.search);

    return qs.extra ? qs.extra : false;
  }

  get renderPreventLeave() {
    const { snapshot } = this.props.AdvertisementEditorStore;
    const currentSnapshot =
      AdvertisementEditorPreventLeaveService.getCurrentSnapshot();
    const { isDrippingDirty } = this.props;

    return (
      <Fragment>
        <ContentPreventLeave
          snapshot={snapshot}
          current={currentSnapshot}
          isForceDirty={isDrippingDirty}
        />
      </Fragment>
    );
  }
  // #endregion

  setMediaUrl = (mediaUrl) => {
    AdvertisementEditorService.mediaVideoUrl = mediaUrl;
  };

  toggleLoader = () => {
    this.setState((prevState) => ({ loaded: !prevState.loaded }));
  };

  forceUpdate = () => {
    this.setState({ state: this.state });
  };

  handleSignLanguageChange = (signLanguage) => {
    this.props.AdvertisementEditorStore.signLanguage = signLanguage;
  };

  handleLanguageChange = (language) => {
    this.props.AdvertisementEditorStore.language = language;
  };

  syncFormChanges = (inputs) => {
    Object.assign(this.props.AdvertisementEditorStore, {
      ...inputs
    });
  };

  onError = (message, data) => UiService.setAlertError(message, data);

  render() {
    const {
      name,
      marketplaceProductId,
      offers,
      affiliateHotlink,
      content,
      dripping,
      published,
      mediaVideoUrl,
      media,
      language,
      signLanguage
    } = this.props.AdvertisementEditorStore;
    const { loaded, firstLoad, drippingErrors } = this.state;
    const {
      match: {
        params: { pageId }
      },
      isDrippingByClassEnabled
    } = this.props;

    const isEditingItem = pageId;
    const formTitle = getContentPageTitle(
      CONTENT_PAGE_TYPE.ADVERTISEMENT,
      isEditingItem
    );

    if (!firstLoad) {
      return <Loader fixed />;
    }

    return (
      <Form
        ref={(node) => (this.formNode = node)}
        onSubmit={this.handleSubmit}
        onChange={this.syncFormChanges.bind(this)}
      >
        <ContentBasePage className="content__grid">
          <ContentBasePage.Header title={formTitle}>
            <ContentBasePage.Header.Actions>
              <ContentBasePage.Header.Actions.Default
                isPublished={published}
                {...this.actions}
              />
            </ContentBasePage.Header.Actions>
          </ContentBasePage.Header>

          <Rerender ref={this.reRenderRef}>
            <div className="advertisement__editor">
              <Grid block>
                <h2>
                  <I18n label="general.content" />
                </h2>
                <p className="mb__md">
                  <I18n label="advertisement_editor.description" />
                </p>

                <FormControl>
                  <InputGroup
                    required
                    ref={this.inputNameRef}
                    name="name"
                    value={name}
                    maxLength="100"
                    autoComplete="off"
                    labelValue="general.name"
                    placeholder="advertisement_editor.advertisement_name"
                  />
                </FormControl>

                <FormControl>
                  <ProductRadiolist
                    required
                    name="product"
                    value={marketplaceProductId}
                    labelValue="advertisement_editor.choose_product"
                  />
                </FormControl>

                <Form.Context>
                  {(context) => (
                    <FormControl>
                      <OfferRadiolist
                        required
                        name="offer"
                        product={context.state.inputs.product}
                        labelValue="advertisement_editor.choose_offer"
                        value={
                          affiliateHotlink ? affiliateHotlink : offers.peek()
                        }
                      />
                    </FormControl>
                  )}
                </Form.Context>

                <FormControl>
                  <EditorGroup
                    name="content"
                    value={content}
                    labelValue="general.description"
                    placeholder="general.editor_placeholder"
                    maxLength={false}
                  />
                </FormControl>

                <FormControl>
                  <MediaUploadGroup
                    name="media"
                    labelValue="general.media"
                    description="general.choose_media"
                    entityType={ENTITY_TYPE.RECOMMENDATION}
                    value={media}
                    mediaUrl={mediaVideoUrl}
                    setMediaUrl={this.setMediaUrl}
                    onCancelUpload={FileService.onCancelUpload}
                    setCentralError={this.onError}
                  />
                </FormControl>

                <Language
                  language={language}
                  signLanguage={signLanguage}
                  onLanguageChange={this.handleLanguageChange}
                  onSignLanguageChange={this.handleSignLanguageChange}
                />
              </Grid>

              {isDrippingByClassEnabled ? (
                <DrippingSection pageType={PageType.Advertisement} />
              ) : (
                <Grid block>
                  <h2>
                    <I18n label="general.configs" />
                  </h2>
                  <label className="label__subtitle mb__md">
                    <I18n label="page_content.configs" />
                  </label>

                  <Accordion opened={true}>
                    <Accordion.Toggle onlyClose />

                    <Accordion.Body>
                      <FormControl>
                        <DrippingGroup
                          name="dripping"
                          value={toJS(dripping)}
                          forceUpdate={this.forceUpdate}
                          errors={drippingErrors}
                        />
                      </FormControl>
                    </Accordion.Body>

                    <Accordion.Toggle />
                  </Accordion>
                </Grid>
              )}
            </div>
          </Rerender>

          <ContentBasePage.Footer onSubmitAndReturn={this.saveThenNavigate}>
            {!isEditingItem && (
              <FormControl>
                <Checkbox
                  id="createAnotherField"
                  name="createAnotherField"
                  label={<I18n label="advertisement_editor.create_another" />}
                  className="_mb-0"
                />
              </FormControl>
            )}
          </ContentBasePage.Footer>
        </ContentBasePage>
        {!loaded && <Loader fixed />}
        {this.renderPreventLeave}
      </Form>
    );
  }
}

export default AdvertisementEditor;
